import { Input } from '@casumo/dojo-react';
import { differenceInYears, format, getDaysInMonth, isFuture, isValid, parse } from 'date-fns';
import React, { memo, useEffect, useMemo, useRef, useState } from 'react';
import ReactInputMask from 'react-input-mask';
import useFetchErrorByKey from '../../api/hooks/useFetchErrorsByKey.tsx';
import { useRegistrationStore } from '../../store/store.ts';
import { TDateInputField } from '../../types/types.ts';
import { TEST_ID } from '../../utils/constants.ts';

export type TDateFormat = 'dd/MM/yyyy' | 'dd.MM.yyyy' | 'MM/dd/yyyy' | 'yyyy/MM/dd';

const DateInput = (fieldProps: TDateInputField & {
  onBlur?: (field: Partial<TDateInputField>) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
}) => {
  const { name, label, onBlur, onKeyDown, dateFormat = 'dd/MM/yyyy', minAge = 18 } = fieldProps;
  const { formData, setFormData, errors, setErrors, setCustomValidations } = useRegistrationStore();
  const getErrorByKey = useFetchErrorByKey;
  const [value, setValue] = useState<string | undefined>(undefined);

  const userUnderAge = getErrorByKey('user_under_age');
  const userAgeError = getErrorByKey('user_age_error');
  const birthdayRequired = getErrorByKey('birthday_required');

  const errorMessages = useMemo(
    () => ({ userUnderAge, userAgeError, birthdayRequired }),
    [userUnderAge, userAgeError, birthdayRequired],
  );

  const renderedMessages = useRef(errorMessages);

  const formatConfig = useMemo(() => {
    const configs: Record<TDateFormat, {
      mask: string;
      placeholder: string;
      indices: { day: number; month: number; year: number };
    }> = {
      'dd/MM/yyyy': {
        mask:        '99 / 99 / 9999',
        placeholder: 'DD / MM / YYYY',
        indices:     { day: 0, month: 1, year: 2 },
      },
      'dd.MM.yyyy': {
        mask:        '99 . 99 . 9999',
        placeholder: 'DD . MM . YYYY',
        indices:     { day: 0, month: 1, year: 2 },
      },
      'MM/dd/yyyy': {
        mask:        '99 / 99 / 9999',
        placeholder: 'MM / DD / YYYY',
        indices:     { day: 1, month: 0, year: 2 },
      },
      'yyyy/MM/dd': {
        mask:        '9999 / 99 / 99',
        placeholder: 'YYYY / MM / DD',
        indices:     { day: 2, month: 1, year: 0 },
      },
    };

    return configs[dateFormat];
  }, [dateFormat]);

  const parseDateByFormat = (parts: string[]) => {
    const { indices } = formatConfig;

    return {
      day:   parts[indices.day],
      month: parts[indices.month],
      year:  parts[indices.year],
    };
  };

  const setErrorAndBlur = (errorMessage: string) => {
    onBlur?.({ name });
    setErrors({ [name]: errorMessage });
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setErrors({ [name]: '' });
    setValue(event.target.value);
  };

  const handleBlur = () => {
    if (!value || value.includes('_')) {
      return setErrorAndBlur(errorMessages.birthdayRequired);
    }

    const parts = (value as string).split('/').map(part => part.trim());
    if (parts.length !== 3) {
      return setErrorAndBlur(errorMessages.userAgeError);
    }

    const { day, month, year } = parseDateByFormat(parts);

    if (!day || !month || !year ||
      day.length !== 2 || month.length !== 2 || year.length !== 4) {
      return setErrorAndBlur(errorMessages.birthdayRequired);
    }

    const monthNum = parseInt(month, 10);
    const dayNum = parseInt(day, 10);
    const yearNum = parseInt(year, 10);

    const dateString = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    const parsedDate = parse(dateString, 'yyyy-MM-dd', new Date());

    if (monthNum < 1 || monthNum > 12) {
      return setErrorAndBlur(errorMessages.userAgeError);
    }

    const daysInMonth = getDaysInMonth(new Date(yearNum, monthNum - 1));
    if (dayNum < 1 || dayNum > daysInMonth) {
      return setErrorAndBlur(errorMessages.userAgeError);
    }

    if (!isValid(parsedDate)) {
      return setErrorAndBlur(errorMessages.birthdayRequired);
    }

    if (isFuture(parsedDate)) {
      return setErrorAndBlur(errorMessages.userAgeError);
    }

    const age = differenceInYears(new Date(), parsedDate);
    if (age < minAge) {
      return setErrorAndBlur(errorMessages.userUnderAge);
    }

    if (age > 120) {
      return setErrorAndBlur(errorMessages.userAgeError);
    }

    const formattedDate = format(parsedDate, dateFormat);
    setFormData({ [name]: formattedDate });
    setErrors({ [name]: '' });
    onBlur?.({ name });
  };

  useEffect(() => {
    if (!formData?.[name]) {
      setCustomValidations({ [name]: errorMessages.birthdayRequired });

      return;
    }

    setCustomValidations({ [name]: '' });
    setErrors({ [name]: '' });
  }, [formData, name, errorMessages.birthdayRequired, setCustomValidations, setErrors]);

  useEffect(() => {
    const previous = renderedMessages.current;
    renderedMessages.current = errorMessages;

    const displayed = errors[name];

    if (!displayed) return;

    const stale = (Object.keys(errorMessages) as (keyof typeof errorMessages)[])
      .find((key) => previous[key] === displayed && errorMessages[key] !== displayed);

    if (stale) {
      setErrors({ [name]: errorMessages[stale] });
    }
  }, [errorMessages, errors, name, setErrors]);

  useEffect(() => {
    setValue(formData?.[name]);
  }, [formData, name]);

  return (
    <ReactInputMask
      id="maskInput"
      mask={formatConfig.mask}
      maskPlaceholder={formatConfig.placeholder}
      placeholder={formatConfig.placeholder}
      maskChar=""
      value={value}
      onChange={handleChange}
      onBlur={handleBlur}
      disabled={false}
    >
      {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
      {/* @ts-ignore: react-input-mask types mismatch */}
      <Input
        id="unique"
        label={label}
        value={value as string}
        name={name}
        inputMode="numeric"
        error={Boolean(errors[name])}
        data-error={Boolean(errors[name])}
        errorMessage={errors?.[name]}
        onKeyDown={onKeyDown}
        clearable
        onClear={() => {
          setValue(undefined);
          setFormData({ [name]: '' });
        }}
        data-testid={TEST_ID.INPUT + name}
        wrapperClass="hide-close-button-on-blur"
      />
    </ReactInputMask>
  );
};

const Memoized = memo(DateInput);
Memoized.displayName = 'DateInput';

export default Memoized;
